import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild  } from '@angular/core';
import { NgForm } from '@angular/forms';

import { Avatars } from '../../classes/avatars';
import { PreferredPronouns } from '../../enums/preferred-pronouns.enum';
import { Student } from '../../interfaces/student';
import { StudentDataService } from '../../services/student-data.service';
import { TelemetryService } from '../../services/telemetry.service';

import { environment } from 'environments/environment';

// Icons
import { faPen, faQuestionCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'shared-student-form',
  templateUrl: './student-form.component.html',
  styleUrls: ['./student-form.component.scss'],
  providers: [Avatars, StudentDataService],
  standalone: false
})
export class StudentFormComponent implements AfterViewInit, OnInit {

  @Input() customerId: number | null = null;
  @Input() student: Student | null = null;
  @Output() studentSavedEvent = new EventEmitter<Student | null>();
  @ViewChild('studentForm') public form: NgForm;

  // Enums
  PreferredPronouns = PreferredPronouns;

  environment = environment;
  isAvatarsShown = false;
  isInvalidSubmitted = false;
  months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  years = [];

  // Progress flags
  errorSaving = '';
  isSaving = false;

  // Icons
  faPen = faPen;
  faQuestionCircle = faQuestionCircle;
  faSpinner = faSpinner;

  constructor(
    private avatars: Avatars,
    private studentDataService: StudentDataService,
    private telemetryService: TelemetryService
  ) { }

  ngOnInit() {

    // Initiate the student's profile
    if (!this.student) {
      this.student = {
        Account: null,
        AvatarPath: null,
        BirthDate: null,
        BirthMonth: null,
        BirthYear: null,
        FirstName: null,
        IsActive: true,
        IsMedicalNotes: false,
        LastName: null,
        MedicalNotes: null,
        MiddleName: null,
        Nickname: null,
        Pronoun: null,
        PronounOther: null,
        StudentId: null
      };
    } else {

      // Parse BirthDate
      this.student.BirthMonth = this.student.BirthDate && this.student.BirthDate.getMonth();
      this.student.BirthYear = this.student.BirthDate && this.student.BirthDate.getFullYear();
    }

    // Set up last 100 years array (start 4 years ago)
    const startYear = new Date().getFullYear() - 4;
    this.years = [...Array.from(Array(100).keys()).map(key => startYear - key)];
  }

  ngAfterViewInit() {

    // Add telemetry
    this.setTelemetry();
  }

  cancel() {

    // Emit output that student form has been canceled
    this.studentSavedEvent.emit(null);
  }

  filterAvatarsByGender(gender: string) {
    return this.avatars.STUDENT_AVATARS.filter(avatar => avatar.charAt(0) === gender);
  }

  onAccountChange() {

    // When an error showed up due to existing account, clear the error if the account is changed
    this.errorSaving = null;
  }

  save() {

    // Immediately leave if we're already saving
    if (!this.form.valid || this.isSaving) {

      // Show required fields
      this.isInvalidSubmitted = !this.form.valid;
      return;
    }

    // Show spinner
    this.isSaving = true;

    // Reset the status
    this.errorSaving = '';

    // Prepare payload to save
    const payload = {
      CustomerId: this.customerId,
      Student: this.student
    };

    // Set the birth date
    payload.Student.BirthDate = new Date(payload.Student.BirthYear, payload.Student.BirthMonth);

    // Create student profile
    this.studentDataService.save(payload)
      .subscribe(
        (value) => {

          // Process value
          this.onSaveSuccess(value);

          // Hide spinner
          this.isSaving = false;
        },
        (error) => {

          // Set the error
          this.errorSaving = error.error;

          // Hide spinner
          this.isSaving = false;
        }
      );
  }

  setAvatar(avatar: string) {
    this.student.AvatarPath = avatar;
    this.isAvatarsShown = false;
  }

  switchAvatars() {
    this.isAvatarsShown = !this.isAvatarsShown;
  }

  private onSaveSuccess(result: any) {

    // Emit output that student has been successfully created
    this.student.Account = result.Account;
    this.student.Age = result.Age;
    this.student.Secret = result.Secret;
    this.student.StudentId = result.StudentId;
    this.studentSavedEvent.emit(this.student);
  }

  private setTelemetry() {
    setTimeout(() => this.telemetryService.inject('CLICK', 'student-form-cancel', null, true));
    setTimeout(() => this.telemetryService.inject('CLICK', 'student-form-submit', null, true));
  }
}
